import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { getCaseStudy } from "@/lib/caseStudiesData";
import { IndustryQuoteForm } from "@/components/IndustryQuoteForm";

export type GrowthPhase = { label: string; title: string; items: string[] };

type Props = {
  eyebrow: string;
  tipsHeading: string;
  tips: { title: string; body: string }[];
  planHeading: string;
  plan: GrowthPhase[];
  caseStudySlugs: string[];
  caseStudyNote?: string;
  links?: { to: string; label: string }[];
  quoteIndustry?: string;
};

/** Tips + 90-day local plan + real case studies (caseStudiesData slugs only). */
export function GrowthPlanSections({
  eyebrow,
  tipsHeading,
  tips,
  planHeading,
  plan,
  caseStudySlugs,
  caseStudyNote,
  links = [],
  quoteIndustry,
}: Props) {
  const studies = caseStudySlugs.map(getCaseStudy).filter(Boolean);
  return (
    <>
      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">{eyebrow}</span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">{tipsHeading}</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {tips.map((t) => (
              <div key={t.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <CheckCircle2 className="h-5 w-5 text-brand-gold" aria-hidden />
                <h3 className="mt-3 text-lg font-bold text-foreground">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/40 py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">{planHeading}</h2>
          <ol className="mt-10 grid gap-5 lg:grid-cols-3">
            {plan.map((p) => (
              <li key={p.label} className="rounded-2xl border border-border bg-card p-6">
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold">{p.label}</span>
                <h3 className="mt-2 text-lg font-bold text-foreground">{p.title}</h3>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  {p.items.map((i) => (
                    <li key={i} className="flex gap-2"><span aria-hidden>•</span>{i}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">Real Colorado results</h2>
          {caseStudyNote && <p className="mt-3 max-w-3xl text-muted-foreground">{caseStudyNote}</p>}
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {studies.map((s) => (
              <Link
                key={s!.slug}
                to="/case-studies/$studySlug"
                params={{ studySlug: s!.slug }}
                className="group rounded-2xl border border-border bg-card p-6 transition hover:border-brand-gold"
              >
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  {s!.industry} · {s!.location}
                </span>
                <p className="mt-2 text-xl font-extrabold text-foreground">{s!.heroResult}</p>
                <p className="mt-2 text-sm text-muted-foreground">{s!.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-foreground group-hover:text-brand-gold">
                  Read the case study <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/free-mockup" className="inline-flex items-center justify-center rounded-full bg-brand-gold px-6 py-3 font-semibold text-foreground">
              Get a Free Website Mockup
            </Link>
            <Link to="/free-audit" className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 font-semibold text-foreground">
              Get a Free Marketing Audit
            </Link>
          </div>
          {links.length > 0 && (
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="font-semibold text-foreground underline underline-offset-4 hover:text-brand-gold">{l.label}</Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    {quoteIndustry && <IndustryQuoteForm industry={quoteIndustry} />}
    </>
  );
}
